import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Plus, Edit, Trash2, X, Upload, ExternalLink, GripVertical } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminCollaboratorsPage = () => {
  const { getAuthHeaders } = useApp();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website_url: '',
    order: 0,
  });

  useEffect(() => {
    fetchCollaborators();
  }, []);

  const fetchCollaborators = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/collaborators`);
      setCollaborators(data);
    } catch (error) {
      toast.error('Failed to fetch collaborators');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo_url: '',
      website_url: '',
      order: collaborators.length,
    });
    setEditingId(null);
  };

  const openDialog = (collaborator = null) => {
    if (collaborator) {
      setFormData({
        name: collaborator.name || '',
        logo_url: collaborator.logo_url || '',
        website_url: collaborator.website_url || '',
        order: collaborator.order || 0,
      });
      setEditingId(collaborator.id);
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/upload/image`, uploadData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      setFormData({ ...formData, logo_url: data.url });
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/collaborators/${editingId}`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success('Collaborator updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/collaborators`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success('Collaborator added!');
      }
      setShowDialog(false);
      resetForm();
      fetchCollaborators();
    } catch (error) {
      toast.error('Failed to save collaborator');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ștergi acest colaborator?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/collaborators/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Collaborator deleted!');
      fetchCollaborators();
    } catch (error) {
      toast.error('Failed to delete collaborator');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-collaborators-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-serif">Colaboratori</h1>
            <p className="text-muted-foreground mt-2">Gestionează logo-urile colaboratorilor din footer</p>
          </div>
          <Button
            onClick={() => openDialog()}
            data-testid="add-collaborator-button"
            className="bg-primary text-primary-foreground"
          >
            <Plus size={18} className="mr-2" />
            Adaugă Colaborator
          </Button>
        </div>

        {/* Collaborators List */}
        {collaborators.length === 0 ? (
          <div className="glass p-12 text-center">
            <p className="text-muted-foreground mb-4">Nu există colaboratori încă.</p>
            <Button onClick={() => openDialog()} variant="outline">
              <Plus size={18} className="mr-2" />
              Adaugă primul colaborator
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collaborators.map((collab) => (
              <div
                key={collab.id}
                className="glass p-6 flex flex-col items-center"
              >
                {/* Logo Preview */}
                <div className="w-full h-32 bg-white/5 border border-white/10 flex items-center justify-center mb-4 overflow-hidden">
                  {collab.logo_url ? (
                    <img
                      src={collab.logo_url}
                      alt={collab.name}
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <span className="text-muted-foreground text-sm">Fără logo</span>
                  )}
                </div>

                {/* Name */}
                <h3 className="text-lg font-medium mb-2">{collab.name || 'Fără nume'}</h3>

                {/* Website */}
                {collab.website_url && (
                  <a
                    href={collab.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1 mb-4"
                  >
                    <ExternalLink size={12} />
                    {collab.website_url}
                  </a>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-auto">
                  <Button onClick={() => openDialog(collab)} variant="outline" size="sm">
                    <Edit size={14} className="mr-1" />
                    Editează
                  </Button>
                  <Button onClick={() => handleDelete(collab.id)} variant="destructive" size="sm">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-slate-950 border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif">
                {editingId ? 'Editează Colaborator' : 'Adaugă Colaborator'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-4 space-y-6">
              {/* Name */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">
                  Nume Colaborator
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Compania XYZ"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary"
                  required
                />
              </div>

              {/* Logo Upload */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">
                  Logo
                </label>
                
                {/* Current Logo Preview */}
                {formData.logo_url && (
                  <div className="mb-3 p-4 bg-white/5 border border-white/10 flex items-center justify-center">
                    <img
                      src={formData.logo_url}
                      alt="Logo preview"
                      className="max-h-20 object-contain"
                    />
                  </div>
                )}

                {/* Upload Button */}
                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-white/20 hover:border-primary transition-colors">
                      <Upload size={18} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {uploadingLogo ? 'Se încarcă...' : 'Încarcă logo'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="hidden"
                    />
                  </label>
                  {formData.logo_url && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setFormData({ ...formData, logo_url: '' })}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>

                {/* Or URL */}
                <div className="mt-3">
                  <label className="text-xs text-muted-foreground mb-1 block">Sau introdu URL logo:</label>
                  <input
                    type="text"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    className="w-full bg-transparent border border-white/20 px-4 py-2 text-sm focus:outline-none focus:border-primary"
                  />
                </div>
              </div>

              {/* Website URL */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">
                  Website (opțional)
                </label>
                <input
                  type="text"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://www.colaborator.ro"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">Lasă gol dacă nu ai un link</p>
              </div>

              {/* Order */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">
                  Ordine afișare
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary"
                />
                <p className="text-xs text-muted-foreground mt-1">Număr mai mic = apare primul</p>
              </div>

              {/* Submit */}
              <div className="flex gap-3">
                <Button type="submit" disabled={loading} className="flex-1 bg-primary text-primary-foreground">
                  {loading ? 'Se salvează...' : editingId ? 'Salvează' : 'Adaugă'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Anulează
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCollaboratorsPage;
